import { useState, useEffect, useCallback } from 'react';
import convert from 'htmr';
import { getDuration, getDates } from '@hiredigital/lib/helpers/date';

import Card from '@hiredigital/ui/Card';
import Button from '@hiredigital/ui/Button';
import TextInput from '@hiredigital/ui/Input/TextInput';
import Select from '@hiredigital/ui/Input/Select';
import InputGroup from '@hiredigital/ui/Form/InputGroup';
import InputContainer from '@hiredigital/ui/Form/InputContainer';

import { useCommon } from '@context/common';

import DatePicker from '@hiredigital/ui/Input/Date/Date';
import Quill from '@hiredigital/ui/Quill/Editor';
import { authRequest } from '@apis/utils';

import Styles from '../Resume.module.scss';
import format from 'date-fns/format';
import isAfter from 'date-fns/isAfter';
import { checkDateValidity } from '@hiredigital/lib/helpers/date';

const EmploymentItem = ({
  employment,
  user,
  updateEmployment,
  updateEmploymentItem,
  deleteEmployment,
}) => {
  const common = useCommon();

  const [id, setId] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [jobTitle, setJobTitle] = useState('');
  const [company, setCompany] = useState('');
  const [topic, setTopic] = useState('');
  const [startMonth, setStartMonth] = useState(null);
  const [startYear, setStartYear] = useState(null);
  const [endMonth, setEndMonth] = useState(null);
  const [endYear, setEndYear] = useState(null);
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateInOrder, setDateInOrder] = useState(true);
  const [isStartDateValid, setIsStartDateValid] = useState(false);
  const [isEndDateValid, setIsEndDateValid] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [topicList, setTopicList] = useState([]);
  const [errors, setErrors] = useState({});

  const formSetters = {
    jobTitle: setJobTitle,
    company: setCompany,
    topic: setTopic,
    startMonth: setStartMonth,
    startYear: setStartYear,
    startDate: setStartDate,
    endDate: setEndDate,
    description: setDescription,
  };

  useEffect(() => {
    handleData(employment);
    setHasMounted(true);

    if (!employment.id) {
      setIsEdit(true);
    }
  }, [handleData, employment]);

  useEffect(() => {
    setTopicList(common?.topics);
  }, [common]);

  const handleData = useCallback((response) => {
    setId(response?.id || null);
    setJobTitle(response?.jobTitle || '');
    setCompany(response?.company || '');
    setTopic((response?.topics?.length && response?.topics[0]) || '');
    setStartMonth(response?.startMonth || null);
    setStartYear(response?.startYear || null);
    setEndMonth(response?.endMonth || null);
    setEndYear(response?.endYear || null);
    setDescription(response?.description || '');
    setStartDate(makeValidDate(response?.startMonth, response?.startYear));
    setEndDate(makeValidDate(response?.endMonth, response?.endYear));
  }, []);

  const makeValidDate = (month, year) => {
    return month && year ? new Date(year, month - 1, 1) : null; // month is zero-based
  };

  const handleEdit = () => {
    setIsEdit(true);
  };

  const handleCancel = () => {
    if (isEdit) {
      setIsEdit(false);
      Object.keys(employment).forEach((k) => {
        formSetters[k] && formSetters[k](employment[k]);
      });
    }
    if (!id) {
      updateEmployment();
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    formSetters[name](value);
    setErrors({});
  };

  const handleStartDateChange = (date) => {
    const startYear = date ? format(date, 'yyyy') : null;
    const startMonth = date ? format(date, 'M') : null;
    setStartDate(date);
    setStartYear(startYear);
    setStartMonth(startMonth);
    setErrors({});
  };

  const handleEndDateChange = (date) => {
    const endYear = date ? format(date, 'yyyy') : null;
    const endMonth = date ? format(date, 'M') : null;
    setEndDate(date);
    setEndYear(endYear);
    setEndMonth(endMonth);
    setErrors({});
  };

  const handleSubmit = () => {
    if (validateRequiredFields()) {
      const data = {
        jobTitle,
        company,
        topics: topic ? [topic.id] : [],
        startMonth,
        startYear,
        endMonth,
        endYear,
        description,
      };

      setLoading(true);
      if (employment.id) {
        const url = `users/${user?.uuid}/employment/${employment.id}/`;
        authRequest.put(url, data).then(
          (response) => {
            handleData(response?.data);
            updateEmploymentItem(response?.data);
            setLoading(false);
            setIsEdit(false);
          },
          (error) => {
            setLoading(false);
          }
        );
      } else {
        const url = `users/${user?.uuid}/employment/`;
        authRequest.post(url, data).then(
          (response) => {
            handleData(response?.data);
            setLoading(false);
            setIsEdit(false);
            updateEmployment(response?.data);
          },
          (error) => {
            setLoading(false);
          }
        );
      }
    } else {
      setHasError(true);
      setSubmitted(false);
    }
  };

  const validateRequiredFields = () => {
    let err = {};

    if (!jobTitle) {
      err = { ...err, jobTitle: 'Enter the job title.' };
    }

    if (!company) {
      err = { ...err, company: 'Enter the company name.' };
    }

    if (!topic?.id) {
      err = { ...err, topic: 'Select an industry.' };
    }

    if (!description) {
      err = { ...err, description: 'Enter the description.' };
    }

    if (!startDate) {
      err = { ...err, startDate: 'Enter the start date.' };
    }

    if (isAfter(startDate, new Date())) {
      err = { ...err, startDate: 'Invalid start date.' };
    }

    if (!checkDateValidity(startDate)) {
      err = { ...err, startDate: 'Invalid start date.' };
    }

    if (startDate && endDate && isAfter(startDate, endDate)) {
      err = { ...err, startDate: 'End date should be later than Start date.' };
    }

    if (Object.keys(err).length > 0) {
      setErrors(err);
      return false;
    }

    return true;
  };

  return (
    <Card.Item>
      {hasMounted && (
        <div>
          {isEdit && (
            <div>
              <div className={Styles.form}>
                <InputGroup>
                  <InputContainer>
                    <TextInput
                      name='jobTitle'
                      label='Job Title'
                      error={errors?.jobTitle}
                      defaultValue={jobTitle}
                      onChange={handleChange}
                    />
                  </InputContainer>

                  <InputContainer>
                    <TextInput
                      name='company'
                      label='Company'
                      error={errors?.company}
                      defaultValue={company}
                      onChange={handleChange}
                    />
                  </InputContainer>
                </InputGroup>
                <InputGroup>
                  <InputContainer>
                    <Select
                      data-test-id='industry'
                      className={Styles.select}
                      defaultValue={topic}
                      error={errors?.topic}
                      classNamePrefix='s-contact'
                      name='topic'
                      label='Industry'
                      getOptionLabel={({ label }) => label}
                      getOptionValue={({ id }) => id}
                      options={topicList}
                      onChange={handleChange}
                    />
                  </InputContainer>
                  <InputGroup>
                    <InputContainer>
                      <DatePicker
                        id='workStartDate'
                        name='startDate'
                        label='From'
                        dateFormat='MM/yyyy'
                        error={errors?.startDate}
                        onChange={handleStartDateChange}
                        // maxDate={endDate}
                        value={startDate}
                        showMonthYearPicker
                      />
                    </InputContainer>

                    <InputContainer>
                      <DatePicker
                        id='workEndDate'
                        name='endDate'
                        label='To'
                        dateFormat='MM/yyyy'
                        onChange={handleEndDateChange}
                        value={endDate}
                        showMonthYearPicker
                      />
                    </InputContainer>
                  </InputGroup>
                </InputGroup>
                <InputContainer>
                  <Quill
                    legacyCompat
                    name='description'
                    label='Job Description'
                    placeholder='Job Description'
                    defaultValue={description}
                    onChange={handleChange}
                    error={errors?.description}
                  />
                </InputContainer>
              </div>

              <div className={Styles.row}>
                <Button
                  name='delete'
                  type={Button.Type.WHITE}
                  onClick={(e) => deleteEmployment(employment)}>
                  {`Delete`}
                </Button>
                <div className={Styles.action}>
                  <Button
                    name='cancel'
                    type={Button.Type.WHITE_BLUE_OUTLINE}
                    onClick={handleCancel}>
                    {`Cancel`}
                  </Button>

                  <Button
                    name='submit'
                    type={Button.Type.BLUE}
                    onClick={handleSubmit}
                    isLoading={loading}>
                    {`Save`}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {!isEdit && (
            <div className={Styles.listPreview}>
              <div className={Styles.row}>
                <div>
                  <p className={Styles.title}>{jobTitle}</p>
                  <p className={Styles.institution}>{company}</p>
                  <p className={Styles.timeframe}>
                    {`${getDates(startMonth, startYear, endMonth, endYear)} ${getDuration(
                      startMonth,
                      startYear,
                      endMonth,
                      endYear
                    )}`}
                  </p>
                </div>
                <div className={Styles.action}>
                  <Button name='Edit' type={Button.Type.BLUE_OUTLINE} onClick={handleEdit}>
                    {`Edit`}
                  </Button>
                </div>
              </div>
              {description && <div className={Styles.description}>{convert(description)}</div>}
            </div>
          )}
        </div>
      )}
    </Card.Item>
  );
};

export default EmploymentItem;
